import { SideTabItem } from '@dealstack/frontend/shared/components/listOld/SideTabItem/SideTabItem';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { memo, useMemo } from 'react';
import styled from 'styled-components';

import { SubListItem } from '@dealstack/frontend/shared/components/listOld/SubListItem/SubListItem';
import type { ISideListConfigEntry } from './PrimarySideList.types';

interface Props {
	className?: string;
	config: ISideListConfigEntry[];
	onActiveChange?: (key: ISideListConfigEntry['key']) => unknown;
	activeKey?: ISideListConfigEntry['key'];
	collapseVisitedStepSublist?: boolean;
	fadeOnLastStep?: boolean;
	lastStepHasStatusVisited?: boolean;
}

export const inlineConfigKeys = (config: ISideListConfigEntry[]) => {
	const result: {
		key: ISideListConfigEntry['key'];
		subitems?: ISideListConfigEntry['key'][];
	}[] = [];

	config.forEach((item) => {
		const subitems = item?.subitems?.map((subitem) => subitem.key);
		result.push({ key: item.key, subitems });
		if (!item.subitems) {
			return;
		}
		item.subitems.forEach((subitem) => {
			result.push({ key: subitem.key });
		});
	});

	return result;
};

const getItemState = (isActiveOrMainItem: boolean, hasAlreadyBeenActive: boolean) => {
	if (isActiveOrMainItem) {
		return 'active';
	}
	if (hasAlreadyBeenActive) {
		return 'inactive';
	}
	return 'visited';
};

const PrimarySideList = ({
	className,
	config,
	activeKey,
	onActiveChange,
	collapseVisitedStepSublist,
	fadeOnLastStep,
	lastStepHasStatusVisited,
}: Props) => {
	const inlinedConfigKeys = inlineConfigKeys(config);
	const shouldFadeSteps =
		inlinedConfigKeys?.length > 0
			? fadeOnLastStep &&
				activeKey === inlinedConfigKeys?.[inlinedConfigKeys?.length - 1]?.key
			: false;

	const stateForKey = useMemo(() => {
		let hasAlreadyBeenActive = false;
		return inlinedConfigKeys
			?.map(({ key, subitems }) => {
				const isActive = key === activeKey;
				const isLast = key === inlinedConfigKeys?.[inlinedConfigKeys?.length - 1]?.key;
				const isActiveMainItem = activeKey != null && subitems?.includes(activeKey);
				if (isActive) {
					hasAlreadyBeenActive = true;
				}
				if (isLast && isActive && lastStepHasStatusVisited) {
					return {
						[key]: 'visited',
					} as Record<string | number, 'inactive' | 'visited' | 'active'>;
				}
				return {
					[key]: getItemState(
						Boolean(isActive || isActiveMainItem),
						Boolean(hasAlreadyBeenActive)
					),
				} as Record<string | number, 'inactive' | 'visited' | 'active'>;
			})
			.reduce((acc, next) => ({ ...acc, ...next }), {});
	}, [activeKey, inlinedConfigKeys]);
	const tree = useMemo(() => {
		return config?.map((entry, index) => {
			const state = stateForKey[entry.key];
			const collapseSublist = state === 'visited' && collapseVisitedStepSublist;
			const childComponents = collapseSublist
				? null
				: entry?.subitems?.map((subEntry) => {
						const state = stateForKey[subEntry.key];

						return (
							<UIElementIdentifier
								identifier={TEST_ELEMENT_IDS.SUB_LIST_TAB}
								key={subEntry.key}
								index={index}
							>
								<SubListItem
									onClick={
										onActiveChange
											? () => onActiveChange(subEntry.key)
											: undefined
									}
									state={state}
									status={subEntry.status}
									title={subEntry.title}
									key={subEntry.key}
								/>
							</UIElementIdentifier>
						);
					});

			return (
				<Entry key={entry.title} data-testid={entry.dataTestId}>
					<SideTabItem
						onClick={onActiveChange ? () => onActiveChange(entry.key) : undefined}
						faded={shouldFadeSteps}
						state={state === 'active' ? state : entry?.state || state}
						status={entry?.status}
						currentState={entry?.state}
					>
						{entry?.title}
					</SideTabItem>
					<ChildrenItems>{childComponents}</ChildrenItems>
				</Entry>
			);
		});
	}, [activeKey, stateForKey, config, onActiveChange, shouldFadeSteps]);

	return <div className={className}>{tree}</div>;
};
const ChildrenItems = styled.div`
	display: flex;
	flex-direction: column;
`;
const Entry = styled.div``;
const Memoized = memo(PrimarySideList);
const Styled = styled(Memoized)`
	display: flex;
	flex-direction: column;
	position: relative;
	z-index: 0;

	${Entry} {
		position: relative;

		${ChildrenItems} {
			margin-top: 0.5rem;
			margin-bottom: 0.5rem;
			margin-left: 1.5rem;

			${SubListItem} {
				margin-bottom: 0.6rem;
			}
		}

		&:not(:last-of-type) {
			&:before {
				position: absolute;
				content: '';
				left: 1.1rem;
				top: 0;
				width: 1px;
				height: 100%;
				background-color: ${({ theme }) => theme.v2.color.neutral[400]};
				z-index: -1;
			}
		}
		&:last-of-type {
			&:before {
				position: absolute;
				content: '';
				left: 1.1rem;
				top: 0;
				width: 1px;
				height: 50%;
				background-color: ${({ theme }) => theme.v2.color.neutral[400]};
				z-index: -1;
			}
		}
	}
`;

export { Styled as PrimarySideList };
